/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { createMemoryBoard, getGridSize, CARD_CATEGORIES, playFlipSound, playMatchSound, playMismatchSound, playWinSound, playSwitchTurnSound, speakCardName } from "@/utils/memoryGameData";
import { RotateCcw, Home, Trophy, Star, Volume2, VolumeX, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import MultiplayerScoreboard from "@/components/MultiplayerScoreboard";
import { useWindowSize } from "@/hooks/useWindowSize";
import { useDevice } from "@/hooks/useDevice";
import { recordGameWon } from "@/utils/playerProfile";
import axios from "axios";

import { logger } from "@/utils/logger";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cards stay still - subtle glow only

export const MemoryGame = ({ 
  category = "hewan", 
  pairCount = 6,
  onBackToMenu,
  isMultiplayer = false,
  player1Name = "Pemain 1",
  player2Name = "Pemain 2",
  playerCount = 2,
  playerNames = ["Pemain 1", "Pemain 2"],
}) => {
  const allNames = playerNames.length >= playerCount ? playerNames.slice(0, playerCount) : [...playerNames, ...Array(playerCount - playerNames.length).fill("").map((_, i) => `Pemain ${playerNames.length + i + 1}`)];
  const [cards, setCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [matchedBy, setMatchedBy] = useState({});
  const [moves, setMoves] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [gameWon, setGameWon] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [lastMatchedId, setLastMatchedId] = useState(null);
  const [shakeCards, setShakeCards] = useState([]);
  const [soundOn, setSoundOn] = useState(true);
  const [currentPlayer, setCurrentPlayer] = useState(0);
  const [scores, setScores] = useState(Array(playerCount).fill(0));
  const [popCards, setPopCards] = useState([]);
  const [saveName, setSaveName] = useState("");
  const [scoreSaved, setScoreSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const PCOLORS = ["#4ECDC4", "#FF6B6B", "#A855F7", "#EAB308"];
  
  const { width, height } = useWindowSize();
  const device = useDevice();
  const gridSize = getGridSize(pairCount);
  const categoryData = CARD_CATEGORIES[category];
  const catColor = categoryData?.color || "#FFD700";

  useEffect(() => { resetGame(); }, [category, pairCount]);

  useEffect(() => {
    let interval;
    if (startTime && !gameWon) {
      interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [startTime, gameWon]);

  const resetGame = () => {
    setCards(createMemoryBoard(category, pairCount));
    setFlippedCards([]);
    setMatchedPairs([]);
    setMatchedBy({});
    setMoves(0);
    setIsLocked(false);
    setGameWon(false);
    setStartTime(Date.now());
    setElapsedTime(0);
    setLastMatchedId(null);
    setShakeCards([]);
    setCurrentPlayer(0);
    setScores(Array(playerCount).fill(0));
    setPopCards([]);
    setSaveName("");
    setScoreSaved(false);
    setSaving(false);
  };

  const handleCardClick = (card) => {
    if (isLocked) return;
    if (flippedCards.find(c => c.uniqueId === card.uniqueId)) return;
    if (matchedPairs.includes(card.id)) return;

    if (soundOn) {
      playFlipSound();
      speakCardName(card.label || card.display);
    }

    const newFlipped = [...flippedCards, card];
    setFlippedCards(newFlipped);

    if (newFlipped.length === 2) {
      setMoves(prev => prev + 1);
      setIsLocked(true);

      if (newFlipped[0].id === newFlipped[1].id) {
        if (soundOn) {
          setTimeout(() => {
            playMatchSound();
            speakCardName(`${card.label || card.display}! Cocok!`);
          }, 400);
        }
        setLastMatchedId(card.id);
        setPopCards([newFlipped[0].uniqueId, newFlipped[1].uniqueId]);
        setTimeout(() => { setLastMatchedId(null); setPopCards([]); }, 800);

        const newMatched = [...matchedPairs, card.id];
        setMatchedPairs(newMatched);
        setMatchedBy(prev => ({ ...prev, [card.id]: currentPlayer }));
        setFlippedCards([]);
        setIsLocked(false);

        if (isMultiplayer) {
          setScores(prev => { const n = [...prev]; n[currentPlayer] = (n[currentPlayer] || 0) + 1; return n; });
        }

        if (newMatched.length === pairCount) {
          setGameWon(true);
          recordGameWon("memory");
          if (soundOn) setTimeout(() => playWinSound(), 400);
        }
      } else {
        if (soundOn) setTimeout(() => playMismatchSound(), 300);
        setShakeCards([newFlipped[0].uniqueId, newFlipped[1].uniqueId]);
        setTimeout(() => {
          setFlippedCards([]);
          setIsLocked(false);
          setShakeCards([]);
          if (isMultiplayer) {
            const next = (currentPlayer + 1) % playerCount;
            setCurrentPlayer(next);
            if (soundOn) {
              playSwitchTurnSound();
              speakCardName(`Turn ${allNames[next]}`);
            }
          }
        }, 1000);
      }
    }
  };

  const isCardFlipped = (card) => flippedCards.find(c => c.uniqueId === card.uniqueId) || matchedPairs.includes(card.id);
  const isCardMatched = (card) => matchedPairs.includes(card.id);
  const isCardShaking = (card) => shakeCards.includes(card.uniqueId);
  const isCardPopping = (card) => popCards.includes(card.uniqueId);
  const getMatchPlayer = (card) => matchedBy[card.id];

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
  const getStarRating = () => { const r = moves / pairCount; return r <= 1.5 ? 3 : r <= 2.5 ? 2 : 1; };
  const getWinner = () => {
    if (!isMultiplayer) return null;
    const maxScore = Math.max(...scores);
    const winners = [];
    scores.forEach((s, i) => { if (s === maxScore) winners.push(i); });
    if (winners.length === playerCount) return null;
    return winners.length === 1 ? allNames[winners[0]] : null;
  };

  const handleSaveScore = async () => {
    if (!saveName.trim() || saving) return;
    setSaving(true);
    try {
      await axios.post(`${API}/game/record`, {
        player_name: saveName.trim(),
        mode: "memory",
        duration_seconds: elapsedTime,
        score: isMultiplayer ? Math.max(...scores) : pairCount,
        stars: getStarRating(),
        category,
        moves_count: moves,
        opponent_type: isMultiplayer ? "pvp" : "solo",
      });
      setScoreSaved(true);
    } catch (e) {
      logger.error("Failed to save:", e);
    }
    setSaving(false);
  };

  const progress = pairCount > 0 ? (matchedPairs.length / pairCount) * 100 : 0;
  const curPlayerName = allNames[currentPlayer];
  const curColor = PCOLORS[currentPlayer] || PCOLORS[0];

  // Card size — DYNAMIC based on viewport width, columns, and device type.
  // Goal: cards fill the available screen space comfortably (fixes iPad "boxes too small" issue).
  const isLargeGrid = pairCount >= 15;
  // Compute optimal card size from viewport
  const cols = gridSize.cols;
  // Horizontal padding budget by device (board container has p-3/p-4 + outer page padding)
  const horizontalPadding = device.type === "mobile" ? 20 : device.type === "tablet" ? 40 : 80;
  const gapPx = isLargeGrid ? 8 : 16;
  // Available width per column = (viewport - padding - gaps) / cols
  const availPerCol = Math.floor(((device.width || 1200) - horizontalPadding - (gapPx * (cols - 1))) / cols);
  // Cap and floor sensible bounds per device. Tablets get the biggest boost.
  const minCard = device.type === "mobile" ? 56 : 72;
  const maxCard = device.type === "mobile" ? 220
    : device.type === "tablet" ? 280   // iPad: much bigger cards
    : 240;                              // desktop
  const sized = Math.max(minCard, Math.min(maxCard, availPerCol));
  const cardMaxW = `${sized}px`;

  return (
    <div className="flex flex-col items-center w-full max-w-7xl mx-auto px-2" data-testid="memory-game-container">
      {gameWon && (
        <>
          <Confetti width={width} height={height}
            colors={["#FFD700", "#FF6B35", "#4ECDC4", "#FF6B6B", "#A855F7", "#FF69B4", catColor]}
            recycle={false} numberOfPieces={isLargeGrid ? 200 : 500} gravity={0.15} initialVelocityY={25}
          />
        </>
      )}

      {/* Header */}
      <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="w-full mb-3">
        <div className="glass-card-strong rounded-2xl p-3 relative overflow-hidden">
          <div className="absolute bottom-0 left-0 h-1 rounded-b-xl transition-all duration-500 ease-out"
            style={{ width: `${progress}%`, background: `linear-gradient(90deg, ${catColor}, #FFD700)` }}
          />
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center gap-2">
              <motion.span className="text-xl"
                animate={{ rotate: [0, -10, 10, 0], scale: [1, 1.1, 1] }}
                transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 2 }}
              >{categoryData?.icon}</motion.span>
              <span className="font-fredoka text-base sm:text-lg" style={{ color: catColor }}>{categoryData?.name}</span>
            </div>
            <div className="flex gap-3 text-xs sm:text-sm">
              <div className="text-gold-light/80">
                <span className="font-fredoka">Langkah: </span>
                <span className="font-fredoka text-white">{moves}</span>
              </div>
              <div className="text-gold-light/80">
                <span className="font-fredoka">Waktu: </span>
                <span className="font-fredoka text-white">{formatTime(elapsedTime)}</span>
              </div>
            </div>
          </div>

          {isMultiplayer ? (
            <div className="flex justify-between items-center">
              <div className="flex gap-2 items-center flex-wrap">
                {allNames.map((name, i) => (
                  <div key={i} className={`flex items-center gap-1.5 px-2 py-1 rounded-lg border-2 transition-all ${currentPlayer === i ? 'scale-105' : 'opacity-60'}`}
                    style={{ borderColor: currentPlayer === i ? PCOLORS[i] : 'transparent', background: currentPlayer === i ? `${PCOLORS[i]}15` : 'transparent' }}>
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: PCOLORS[i] }} />
                    <span className="font-fredoka text-xs font-bold" style={{ color: PCOLORS[i] }}>{name}</span>
                    <span className="font-fredoka text-sm font-bold" style={{ color: PCOLORS[i] }}>{scores[i] || 0}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-1.5">
                <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="border-gold/20 text-gold hover:bg-gold/10 h-8 w-8 p-0" data-testid="memory-sound-toggle">
                  {soundOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
                </Button>
                <Button onClick={resetGame} variant="outline" size="sm" className="border-gold/20 text-gold hover:bg-gold/10 h-8 px-2" data-testid="memory-reset-btn">
                  <RotateCcw className="w-3.5 h-3.5 mr-1" />Ulang
                </Button>
                <Button onClick={onBackToMenu} variant="outline" size="sm" className="border-gold/20 text-gold hover:bg-gold/10 h-8 px-2" data-testid="memory-menu-btn">
                  <Home className="w-3.5 h-3.5 mr-1" />Menu
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex justify-between items-center">
              <div className="text-gold-light/80 text-sm font-fredoka">
                Pasangan: <span style={{ color: catColor }} className="font-bold">{matchedPairs.length}</span> / {pairCount}
              </div>
              <div className="flex gap-1.5">
                <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="border-gold/20 text-gold hover:bg-gold/10 h-8 w-8 p-0" data-testid="memory-sound-toggle">
                  {soundOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
                </Button>
                <Button onClick={resetGame} variant="outline" size="sm" className="border-gold/20 text-gold hover:bg-gold/10 h-8 px-2" data-testid="memory-reset-btn">
                  <RotateCcw className="w-3.5 h-3.5 mr-1" />Ulang
                </Button>
                <Button onClick={onBackToMenu} variant="outline" size="sm" className="border-gold/20 text-gold hover:bg-gold/10 h-8 px-2" data-testid="memory-menu-btn">
                  <Home className="w-3.5 h-3.5 mr-1" />Menu
                </Button>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Turn indicator multiplayer */}
      {isMultiplayer && (
        <motion.div key={currentPlayer} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
          className="mb-2 px-4 py-1.5 rounded-full border-2 font-fredoka text-sm font-bold"
          style={{ borderColor: curColor, color: curColor, background: `${curColor}15` }}
          data-testid="memory-turn-indicator"
        >
          Giliran: {curPlayerName}
        </motion.div>
      )}

      {/* Game Board - FULL SIZE with moving effects */}
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="rounded-xl p-4 sm:p-6 border-2 relative w-full"
        style={{
          background: 'rgba(30,20,10,0.85)',
          borderColor: isMultiplayer ? `${curColor}44` : `${catColor}33`,
          boxShadow: `0 0 20px ${isMultiplayer ? curColor : catColor}15`,
          transition: 'border-color 0.3s',
        }}
      >
        <div
          className={`grid ${isLargeGrid ? 'gap-1.5 sm:gap-2' : 'gap-3 sm:gap-4'} justify-center mx-auto`}
          style={{
            gridTemplateColumns: `repeat(${gridSize.cols}, 1fr)`,
            maxWidth: `${gridSize.cols * (sized + (isLargeGrid ? 8 : 16))}px`,
          }}
        >
          {cards.map((card, index) => {
            const matchPlayer = getMatchPlayer(card);
            const matchColor = matchPlayer !== undefined ? (PCOLORS[matchPlayer] || '#4ade80') : '#4ade80';
            const flipped = isCardFlipped(card);
            const matched = isCardMatched(card);
            const shaking = isCardShaking(card);
            const popping = isCardPopping(card);

            return (
              <motion.button
                key={card.uniqueId}
                data-testid={`memory-card-${card.uniqueId}`}
                onClick={() => handleCardClick(card)}
                disabled={isLocked || matched}
                initial={{ scale: 0, opacity: 0 }}
                animate={{
                  scale: shaking ? [1, 1.05, 0.95, 1.05, 0.95, 1] : popping ? [1, 1.2, 1] : 1,
                  opacity: 1,
                  x: shaking ? [0, -5, 5, -5, 5, 0] : 0,
                }}
                transition={{
                  scale: { delay: isLargeGrid ? 0 : index * 0.03, duration: 0.2 },
                  opacity: { delay: isLargeGrid ? 0 : index * 0.03, duration: 0.2 },
                  x: shaking ? { duration: 0.4 } : {},
                }}
                className="relative cursor-pointer aspect-square w-full"
                style={{ perspective: '800px', maxWidth: cardMaxW }}
                whileHover={!flipped ? { scale: 1.08, zIndex: 10 } : {}}
                whileTap={!flipped ? { scale: 0.92 } : {}}
              >
                <AnimatePresence mode="wait">
                  {flipped ? (
                    <motion.div
                      key="front"
                      initial={{ rotateY: 90, scale: 0.8 }}
                      animate={{ rotateY: 0, scale: popping ? [1, 1.2, 1] : 1 }}
                      exit={{ rotateY: 90, scale: 0.8 }}
                      transition={{ duration: 0.3, scale: { duration: 0.5 } }}
                      className="absolute inset-0 rounded-xl flex flex-col items-center justify-center border-3 overflow-hidden"
                      style={{
                        backfaceVisibility: 'hidden',
                        borderColor: matched ? matchColor : card.color || catColor,
                        borderWidth: '3px',
                        background: matched
                          ? `linear-gradient(145deg, ${matchColor}35, ${matchColor}55, ${matchColor}30)`
                          : `linear-gradient(145deg, ${card.color || catColor}20, ${card.color || catColor}40, ${card.color || catColor}15)`,
                        boxShadow: matched
                          ? `0 0 24px ${matchColor}60, inset 0 0 12px ${matchColor}20`
                          : `0 0 16px ${card.color || catColor}50, inset 0 0 20px rgba(255,255,255,0.08)`,
                      }}
                    >
                      {/* Bright card color accent at top */}
                      <div className="absolute top-0 left-0 right-0 h-1.5 rounded-t-xl" style={{ background: card.color || catColor }} />
                      <motion.span className={isLargeGrid ? "text-3xl sm:text-4xl drop-shadow-lg" : "text-5xl sm:text-6xl md:text-7xl drop-shadow-lg"}
                        animate={matched ? { scale: [1, 1.15, 1] } : {}}
                        transition={{ duration: 0.3 }}
                        style={{ filter: `drop-shadow(0 2px 8px ${card.color || catColor}80)` }}
                      >{card.display}</motion.span>
                      {card.label && (
                        <span className={isLargeGrid ? "text-[7px] sm:text-[9px] mt-0.5 font-fredoka font-bold px-1 py-0.5 rounded-full" : "text-[9px] sm:text-xs md:text-sm mt-1 font-fredoka font-bold px-2 py-0.5 rounded-full"}
                          style={{
                            color: 'white',
                            background: `${card.color || catColor}90`,
                            textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                          }}
                        >{card.label}</span>
                      )}
                      {/* Sparkle particles for matched cards - reduced for large grids */}
                      {matched && !isLargeGrid && (
                        <div className="absolute inset-0 pointer-events-none overflow-hidden">
                          {[...Array(4)].map((_, i) => (
                            <motion.div key={i}
                              className="absolute w-1.5 h-1.5 rounded-full"
                              style={{ background: matchColor }}
                              animate={{
                                opacity: [0, 1, 0],
                                x: [`50%`, `${10 + Math.random() * 80}%`],
                                y: [`50%`, `${10 + Math.random() * 80}%`],
                                scale: [0, 1.5, 0],
                              }}
                              transition={{ duration: 1.8, repeat: Infinity, delay: i * 0.4 }}
                            />
                          ))}
                        </div>
                      )}
                    </motion.div>
                  ) : (
                    <motion.div
                      key="back"
                      initial={{ rotateY: -90 }}
                      animate={{ rotateY: 0 }}
                      exit={{ rotateY: -90 }}
                      transition={{ duration: 0.25 }}
                      className="absolute inset-0 rounded-xl flex flex-col items-center justify-center border-2 overflow-hidden"
                      style={{
                        backfaceVisibility: 'hidden',
                        background: `linear-gradient(145deg, ${catColor}EE, ${catColor}CC, ${catColor}DD)`,
                        borderColor: `${catColor}AA`,
                        boxShadow: `0 4px 20px ${catColor}30`,
                      }}
                    >
                      {/* Subtle pattern */}
                      <div className="absolute inset-0 opacity-15"
                        style={{
                          backgroundImage: `radial-gradient(circle at 30% 30%, white 1px, transparent 1px), radial-gradient(circle at 70% 70%, white 1px, transparent 1px)`,
                          backgroundSize: '14px 14px',
                        }}
                      />
                      <span className={isLargeGrid ? "text-2xl sm:text-3xl relative z-10 drop-shadow-lg" : "text-3xl sm:text-4xl md:text-5xl relative z-10 drop-shadow-lg"}
                      >{categoryData?.icon || "?"}</span>
                      <span className="text-[7px] sm:text-[9px] font-fredoka font-bold mt-0.5 opacity-60 relative z-10 text-white">?</span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.button>
            );
          })}
        </div>
      </motion.div>

      {/* Encouragement */}
      <AnimatePresence>
        {lastMatchedId && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: [1, 1.1, 1] }}
            exit={{ opacity: 0, y: -10 }}
            className="mt-3 font-fredoka text-base font-bold"
            style={{ color: isMultiplayer ? curColor : catColor }}
          >
            {isMultiplayer ? `${curPlayerName} Cocok! Giliran lagi!` : 'Cocok! Hebat!'}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Win Modal */}
      <AnimatePresence>
        {gameWon && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0 }}
              transition={{ type: "spring", damping: 15 }}
              className="rounded-2xl p-6 sm:p-8 border-2 text-center max-w-md w-full"
              style={{ background: 'rgba(30,20,10,0.95)', borderColor: `${catColor}55`, boxShadow: `0 0 60px ${catColor}30` }}
            >
              <motion.div animate={{ rotate: [0, -10, 10, 0], scale: [1, 1.1, 1] }} transition={{ duration: 0.6, repeat: 2 }}>
                <Trophy className="w-16 h-16 mx-auto mb-4" style={{ color: catColor }} />
              </motion.div>
              
              {isMultiplayer ? (
                <>
                  <h2 className="font-fredoka text-2xl sm:text-3xl font-bold mb-3" style={{ color: catColor }}>
                    {getWinner() ? `${getWinner()} Menang!` : 'Seri!'}
                  </h2>
                  <div className="flex justify-center gap-4 mb-4 flex-wrap">
                    {allNames.map((name, i) => (
                      <div key={i} className="text-center">
                        <div className="w-3 h-3 rounded-full mx-auto mb-1" style={{ background: PCOLORS[i] }} />
                        <p className="font-fredoka text-sm font-bold" style={{ color: PCOLORS[i] }}>{name}</p>
                        <p className="font-fredoka text-2xl font-bold" style={{ color: PCOLORS[i] }}>{scores[i] || 0}</p>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <h2 className="font-fredoka text-2xl sm:text-3xl font-bold mb-2" style={{ color: catColor }}>Hebat!</h2>
                  <p className="font-fredoka text-gold-light/80 mb-4">Berhasil mencocokkan semua kartu {categoryData?.name}!</p>
                  <div className="flex justify-center gap-1 mb-4">
                    {[1, 2, 3].map((star) => (
                      <motion.div key={star} initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: star * 0.2, type: "spring" }}>
                        <Star className={`w-10 h-10 ${star <= getStarRating() ? 'fill-current' : ''}`}
                          style={{ color: star <= getStarRating() ? catColor : '#3a2a1a' }}
                        />
                      </motion.div>
                    ))}
                  </div>
                </>
              )}

              <div className="space-y-1 mb-5 text-sm">
                <p className="font-fredoka text-gold-light/80">Langkah: <span className="font-bold" style={{ color: catColor }}>{moves}</span></p>
                <p className="font-fredoka text-gold-light/80">Waktu: <span className="font-bold" style={{ color: catColor }}>{formatTime(elapsedTime)}</span></p>
              </div>

              <div className="flex flex-col gap-2">
                {!scoreSaved ? (
                  <div className="flex gap-2 mb-1">
                    <input
                      data-testid="memory-save-name-input"
                      type="text"
                      value={saveName}
                      onChange={(e) => setSaveName(e.target.value)}
                      placeholder="Nama kamu..."
                      maxLength={50}
                      className="flex-1 glass-card border-gold/20 rounded-xl px-3 py-2 text-white font-fredoka text-sm focus:outline-none focus:border-gold/40"
                      onKeyDown={(e) => e.key === 'Enter' && handleSaveScore()}
                    />
                    <Button onClick={handleSaveScore} disabled={!saveName.trim() || saving}
                      className="font-fredoka font-bold text-brown-dark px-4" style={{ background: '#4ade80' }}
                      data-testid="memory-save-score-btn">
                      <Save className="w-4 h-4 mr-1" />{saving ? '...' : 'Simpan'}
                    </Button>
                  </div>
                ) : (
                  <p className="text-green-400 font-fredoka text-sm mb-1" data-testid="memory-score-saved">Skor tersimpan!</p>
                )}
                <Button onClick={resetGame} className="w-full font-fredoka font-bold text-brown-dark" style={{ background: catColor }} data-testid="memory-play-again-btn">
                  <RotateCcw className="w-4 h-4 mr-2" />Main Lagi
                </Button>
                <Button onClick={onBackToMenu} variant="outline" className="w-full glass-card border-gold/20 text-gold hover:bg-gold/10 font-fredoka rounded-xl" data-testid="memory-back-category-btn">
                  <Home className="w-4 h-4 mr-2" />Pilih Kategori
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MemoryGame;
